"use client";

import { useParams, usePathname } from "next/navigation";
import { useEffect } from "react";
import { useEventOptions } from "../_logic/EventOptionsContext";
import { deriveSidebarMode } from "../_logic/navigation";
import { useEnterpriseStore } from "../_logic/store";

/**
 * The event the UI should act on, whether or not the URL names one.
 *
 * Resolution order:
 *  1. the `event_id` in the URL,
 *  2. the last event this tab looked at, if it is still one of the user's
 *     options AND this route has not left event mode,
 *  3. null.
 *
 * Step 2 is what lets the sidebar stay in event mode across a hop through Chat,
 * which has no event in its path. The membership check is load-bearing: an
 * active-org switch swaps `eventOptions`, and without it a remembered event
 * from the previous org would come back to life.
 *
 * The mode check is the other half of that. Chat and `/dashboard/organization/*`
 * are both event-less paths, but they mean opposite things: Chat is *inside* an
 * event, org routes are an explicit exit from one. `SECTION_TO_MODE` already
 * draws that line (`organization: "org"`, with `chat` deliberately absent so it
 * inherits "stay where you were"), so read it rather than re-deriving it. Without
 * this, standing on the org Events page left the palette scoped to whatever event
 * you last opened, and offering to act on it.
 *
 * An org route also *clears* the memory rather than only ignoring it, so the
 * exit outlives the route itself. Chat inherits "stay where you were" from
 * whichever side you arrived from, which is what the sidebar does too.
 *
 * Shared rather than reimplemented per caller. The command palette needs the
 * same answer the sidebar shows, and a palette that navigates to a different
 * event than the sidebar highlights is a miserable bug to find.
 *
 * @returns The resolved event id, or null when there is nothing to act on.
 */
export function useResolvedEventId(): string | null {
  // Hooks
  const params = useParams();
  const pathname = usePathname();
  const eventOptions = useEventOptions();
  const lastEventId = useEnterpriseStore((state) => state.lastEventId);
  const setLastEventId = useEnterpriseStore((state) => state.setLastEventId);

  // Derived values
  const currentEventId = typeof params.event_id === "string" ? params.event_id : null;
  const hasLeftEventMode = deriveSidebarMode(pathname ?? "") === "org";
  const remembered =
    !hasLeftEventMode && lastEventId && eventOptions.some((option) => option.value === lastEventId)
      ? lastEventId
      : null;

  // Effects
  // An org route does not just stop acting on the remembered event, it forgets
  // it. Otherwise the exit only lasts as long as you stand on the org route:
  // hopping from there to Chat brought the event back, because Chat is not an
  // org path. The sidebar does not come back either (`showEventMode` also gates
  // on its own click-driven mode, which organization sets to "org"), so without
  // this the palette claimed an event the sidebar was no longer showing, which
  // is the same disagreement in the opposite direction.
  useEffect(() => {
    if (currentEventId) {
      setLastEventId(currentEventId);
      return;
    }
    if (hasLeftEventMode) setLastEventId(null);
  }, [currentEventId, hasLeftEventMode, setLastEventId]);

  return currentEventId ?? remembered;
}
